<template>
  <transition name="task-detail-dialog">
    <div v-if="open" class="task-detail-dialog" @click.self="$emit('close')">
      <aside class="task-detail-dialog__panel" role="dialog" aria-modal="true">
        <header class="task-detail-dialog__header">
          <div class="task-detail-dialog__heading">
            <div class="task-detail-dialog__title">
              {{ task?.name || 'Детали задачи' }}
            </div>
            <div v-if="task?.id" class="task-detail-dialog__subtitle">
              Задача #{{ task.id }}
            </div>
          </div>
          <button
            type="button"
            class="task-detail-dialog__close"
            @click="$emit('close')"
            aria-label="Закрыть"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6L18 18M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div class="task-detail-dialog__body">
          <template v-if="loading">
            <div class="task-detail-dialog__state">Загрузка...</div>
          </template>

          <template v-else-if="error">
            <div class="task-detail-dialog__state task-detail-dialog__state--error">
              {{ error }}
            </div>
          </template>

          <template v-else>
            <section class="task-detail-dialog__section">
              <h3 class="task-detail-dialog__section-title">Описание</h3>
              <bbob-bbcode
                v-if="detail?.description"
                container="div"
                class="task-detail-dialog__description"
                :plugins="bbobPlugins"
              >
                {{ detail.description }}
              </bbob-bbcode>
              <div v-else class="task-detail-dialog__state">Описание отсутствует.</div>
            </section>

            <section v-if="hasCheckList"  class="task-detail-dialog__section">
              <h3 class="task-detail-dialog__section-title">Чек-лист</h3>
              <div class="task-detail-dialog__checklist">
                <task-check-list-tree :check-list="detail.checkList" />
              </div>
            </section>
          </template>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import { Component as BbobBbcode } from '@bbob/vue3'
import presetVue from '@bbob/preset-vue'
import TaskCheckListTree from '@/components/TaskCheckListTree'

const BBOB_ALIAS_TAGS = ['b', 'i', 'u', 's', 'url', 'img', 'quote', 'code', 'style', 'list', 'color']

const bbobPlugins = [
  presetVue.extend(tags => {
    const upperCaseAliases = BBOB_ALIAS_TAGS.reduce((aliases, tagName) => {
      if (typeof tags[tagName] === 'function') {
        aliases[tagName.toUpperCase()] = tags[tagName]
      }
      return aliases
    }, {})

    return {
      ...tags,
      ...upperCaseAliases,
    }
  })(),
]

export default {
  name: 'TaskDetailDialog',
  emits: ['close'],
  components: {
    BbobBbcode,
    TaskCheckListTree,
  },
  data() {
    return {
      bbobPlugins,
    }
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    detail: {
      type: Object,
      default: null,
    },
    task: {
      type: Object,
      default: null,
    },
    error: {
      type: String,
      default: '',
    },
  },
  computed: {
    hasCheckList() {
      return Array.isArray(this.detail?.checkList) && this.detail.checkList.length > 0
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscapeKeydown)
  },
  unmounted() {
    document.removeEventListener('keydown', this.handleEscapeKeydown)
  },
  methods: {
    handleEscapeKeydown(event) {
      if (this.open !== true) {
        return
      }

      if (event.key === 'Escape') {
        this.$emit('close')
      }
    },
  },
}
</script>

<style scoped lang="scss">
.task-detail-dialog {
  position: fixed;
  inset: 0;
  z-index: 110;
  background: rgba(15, 23, 42, 0.24);
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;

  &__panel {
    width: 60vw;
    margin-top: auto;
    height: 95vh;
    min-width: 520px;
    background: #fff;
    border-radius: 14px 0 0 0;
    box-shadow: 0 16px 50px rgba(15, 23, 42, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid #e2e8f0;
    padding: 16px 20px;
  }

  &__heading {
    min-width: 0;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__subtitle {
    margin-top: 4px;
    font-size: 12px;
    color: #64748b;
  }

  &__close {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    background: #e2e8f0;
    color: #334155;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 14px;
      height: 14px;
      stroke: currentColor;
      stroke-width: 2.2;
      fill: none;
      stroke-linecap: round;
    }
  }

  &__body {
    padding: 18px 20px 22px;
    overflow-y: auto;
  }

  &__section {
    & + & {
      margin-top: 22px;
    }
  }

  &__section-title {
    margin: 0 0 10px 0;
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
  }

  &__state {
    color: #64748b;
    font-size: 14px;

    &--error {
      color: #b91c1c;
    }
  }

  &__description {
    font-size: 14px;
    line-height: 1.45;
    color: #1e293b;
    white-space: break-spaces;

    :deep(ul),
    :deep(ol) {
      margin: 0 0 0 18px;
      list-style: square;

      white-space: normal;
    }

    :deep(li + li) {
      margin-top: 4px;
    }

    :deep(li:has(> ul)),
    :deep(li:has(> ol)) {
      list-style: none;
    }

    :deep(img) {
      max-width: 100%;
      margin-top: 8px;
      border-radius: 8px;
    }

    :deep(blockquote) {
      border-left: 3px solid #94a3b8;
      margin: 8px 0;
      padding: 6px 10px;
      color: #334155;
      background: #f8fafc;
    }

    :deep(details) {
      margin: 8px 0;
    }

    :deep(pre) {
      background: #0f172a;
      color: #e2e8f0;
      border-radius: 8px;
      padding: 10px;
      overflow-x: auto;
      margin: 10px 0;
    }

    :deep(a) {
      color: #0b67aa;
      text-decoration: underline;
    }
  }

  &-enter-active,
  &-leave-active {
    transition: opacity 0.22s ease;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }

  &-enter-active &__panel,
  &-leave-active &__panel {
    transition: transform 0.24s ease;
  }

  &-enter-from &__panel,
  &-leave-to &__panel {
    transform: translateX(100%);
  }
}

@media (max-width: 1100px) {
  .task-detail-dialog {
    &__panel {
      width: 100vw;
      min-width: 0;
      margin-top: 12vh;
      height: 88vh;
      border-radius: 14px 14px 0 0;
    }
  }
}
</style>
